import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Spinner,
} from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { PaymentPagePreview } from '@/components/features/services';
import { ThemeEditor } from '@/components/features/services/theme/theme-editor';
import { ThemeShareWarningDialog } from '@/components/features/services/theme/theme-share-warning-dialog';
import { useStylesheetKnobs } from '@/components/features/services/theme/use-stylesheet-knobs';
import { useSaveTheme, SaveThemeError } from '@/components/features/services/theme/use-save-theme';
import { useService } from '@/hooks/use-services';
import { useMerchantAssets } from '@/hooks/use-merchant-assets';
import { useAuth } from '@/hooks/use-auth';
import { type ThemeKnobs, DEFAULT_KNOBS } from '@/lib/theme/theme-knobs';
import { generateTwoZoneV2 } from '@/lib/theme/generate-stylesheet';
import { ROLE_GROUPS } from '@/types/auth';

/**
 * Theme editor page (Spec 340 + Issue #341): split view — knob panel left, live
 * payment-page preview right. Seeds the editor from the service's effective
 * stylesheet (linked asset file, else inline URL) via the `@theme-knobs` round-trip,
 * gates write controls on SERVICE_WRITE, and saves through {@link useSaveTheme}
 * (generate → upload → asset → link). A shared stylesheet asset (usedBy > 1) forks
 * a per-service copy after an explicit confirm rather than mutating the shared file.
 */
export function ThemePage() {
  const { t } = useTranslation('service-theme');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const merchantIdHint = (location.state as { merchantId?: string } | null)?.merchantId;

  const { user } = useAuth();
  const canWrite = !!user && (ROLE_GROUPS.SERVICE_WRITE as string[]).includes(user.role);

  const { data: service, isLoading, error, refetch } = useService(id ?? '', merchantIdHint);

  const merchantId = service?.merchantId;
  const stylesheetAssetId = service?.theme?.stylesheetAssetId;

  // Stylesheet assets — resolve the linked asset's file URL (round-trip after the
  // URL→Asset migration empties the inline URL) and read its `usedBy` for the fork.
  const assetsQuery = useMerchantAssets(merchantId, 'stylesheet');
  const currentAsset = stylesheetAssetId
    ? assetsQuery.data?.find((a) => a.assetId === stylesheetAssetId)
    : undefined;

  // Effective stylesheet source: the linked asset's file wins over the inline URL.
  const effectiveStylesheetUrl = currentAsset?.currentFileUrl ?? service?.theme?.stylesheetUrl;

  // Don't seed until we know whether a referenced asset resolves — otherwise a
  // late-arriving asset list would reseed (clobber) in-progress edits.
  const assetsSettled = !merchantId || !stylesheetAssetId || assetsQuery.isFetched;

  const seed = useStylesheetKnobs(assetsSettled ? effectiveStylesheetUrl : undefined);
  const saveTheme = useSaveTheme(service);

  const [knobs, setKnobs] = useState<ThemeKnobs>(DEFAULT_KNOBS);
  // Reseed whenever the service / its effective stylesheet changes (covers in-session
  // navigation and the post-save refetch).
  const seedKey = assetsSettled ? `${service?.serviceId ?? ''}|${effectiveStylesheetUrl ?? ''}` : null;
  const [seededKey, setSeededKey] = useState<string | null>(null);
  const [forkDialogOpen, setForkDialogOpen] = useState(false);
  // Issue #417: after a successful save we hold the just-saved knobs as the baseline.
  // The post-save refetch invalidates the service AND the asset list; they can land
  // out of order, so for a moment `effectiveStylesheetUrl` may still resolve a stale
  // stylesheet. Without this hold the reseed below would roll the editor back to the
  // old knobs (the Theme-Editor rollback). We release the hold once the canonical seed
  // re-derives the saved knobs (the @theme-knobs round-trip is exact).
  const [pendingSavedKnobs, setPendingSavedKnobs] = useState<ThemeKnobs | null>(null);

  useEffect(() => {
    if (!seedKey || isLoading || !service || seed.status !== 'ready') return;
    if (pendingSavedKnobs) {
      if (JSON.stringify(seed.knobs) === JSON.stringify(pendingSavedKnobs)) {
        setSeededKey(seedKey);
        setPendingSavedKnobs(null);
      }
      return; // never reseed away from a just-saved baseline
    }
    if (seededKey !== seedKey) {
      setKnobs(seed.knobs);
      setSeededKey(seedKey);
    }
  }, [isLoading, service, seed.status, seed.knobs, seedKey, seededKey, pendingSavedKnobs]);

  // A failed save releases the hold so the editor returns to normal seeding.
  useEffect(() => {
    if (saveTheme.isError) setPendingSavedKnobs(null);
  }, [saveTheme.isError]);

  const seeded = pendingSavedKnobs !== null || (seededKey !== null && seededKey === seedKey);
  const dirty =
    pendingSavedKnobs !== null
      ? JSON.stringify(knobs) !== JSON.stringify(pendingSavedKnobs)
      : seeded && JSON.stringify(knobs) !== JSON.stringify(seed.knobs);

  const backgroundUrl = service?.theme?.backgroundImageUrl ?? null;
  const rawCss = useMemo(
    () => generateTwoZoneV2(knobs, { backgroundUrl }),
    [knobs, backgroundUrl],
  );

  // A linked stylesheet shared by other services (or with underivable usage) is left
  // untouched on save — only this service moves to a new asset. When this service
  // solely owns its asset, retire that predecessor after the relink (Issue #417).
  const isShared =
    !!currentAsset && ((currentAsset.usedBy.length > 1) || !!currentAsset.usageUnavailable);

  const doSave = () => {
    setForkDialogOpen(false);
    const retireAssetId = currentAsset && !isShared ? currentAsset.assetId : undefined;
    // Pin the just-saved knobs so the post-save refetch can't roll the editor back.
    setPendingSavedKnobs(knobs);
    saveTheme.mutate({ knobs, retireAssetId });
  };

  const handleSave = () => {
    // The share dialog is now purely informational: every save creates a new asset,
    // so there is no in-place mutation of a shared file to guard against. We still
    // surface it in the shared case so the operator knows the others are untouched;
    // non-shared saves go straight through.
    if (isShared) setForkDialogOpen(true);
    else doSave();
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/services')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('back')}
        </Button>
        <ApiErrorDisplay
          error={error ?? new Error('Service not found')}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  // Spec 340 v1 is scoped to two-zone-v2 only (spec "Out of Scope").
  if (service.themeLayout !== 'two-zone-v2') {
    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            navigate(`/services/${service.serviceId}`, { state: { merchantId: service.merchantId } })
          }
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('back')}
        </Button>
        <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-medium">{t('unsupported.title')}</p>
          <p className="mt-1">{t('unsupported.note')}</p>
        </div>
      </div>
    );
  }

  // Preview from the live knobs (rawCss); omit stylesheetUrl so the loader stays idle.
  const previewTheme = { ...service.theme, stylesheetUrl: undefined };

  const saveError = saveTheme.error;
  const saveErrorKey =
    saveError instanceof SaveThemeError ? `save.error.${saveError.stage}` : 'save.error.generic';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate(`/services/${service.serviceId}`, { state: { merchantId: service.merchantId } })
            }
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('back')}
          </Button>
          <h1 className="mt-3 text-xl font-semibold">{t('title')}</h1>
          <p className="text-sm text-muted-foreground">
            {service.displayName || service.description}
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('editor.title')}</CardTitle>
            {!canWrite && <CardDescription>{t('readOnly.note')}</CardDescription>}
          </CardHeader>
          <CardContent>
            <ThemeEditor
              knobs={knobs}
              onChange={setKnobs}
              backgroundUrl={backgroundUrl}
              disabled={!canWrite}
              dirty={dirty}
              source={seed.source}
              onReset={() => setKnobs(seed.knobs)}
              onSave={canWrite ? handleSave : undefined}
              saving={saveTheme.isPending}
            />
            {saveTheme.isError && (
              <div role="alert" className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                {t(saveErrorKey)}
              </div>
            )}
            {saveTheme.isSuccess && !dirty && (
              <p className="mt-4 text-sm text-emerald-700">{t('save.success')}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('preview.title')}</CardTitle>
            <CardDescription>{t('preview.approxNote')}</CardDescription>
          </CardHeader>
          <CardContent>
            <PaymentPagePreview
              description={service.description}
              grossAmount={service.grossAmount}
              subscriptionInterval={service.subscriptionInterval}
              theme={previewTheme}
              themeLayout="two-zone-v2"
              rawCss={rawCss}
            />
          </CardContent>
        </Card>
      </div>

      <ThemeShareWarningDialog
        open={forkDialogOpen}
        onOpenChange={setForkDialogOpen}
        usedBy={currentAsset?.usedBy ?? []}
        usageUnavailable={currentAsset?.usageUnavailable}
        onConfirm={doSave}
        isPending={saveTheme.isPending}
      />
    </div>
  );
}
